import { PhoneOff, LogOut } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AuroraLogo, AuroraWordmark } from "@/components/AuroraLogo";
import { EncryptedBadge } from "@/components/EncryptedBadge";
import { NoTraceTag } from "@/components/room/Privacy";
import { InviteQrDialog } from "@/components/room/InviteQrDialog";
import { SupportButton } from "@/components/SupportButton";

export const RoomHeader = ({ room, isHost, status, rtt, onEnd, onLeave }) => {
  const stColor = status === "online" ? "var(--au-green)" : "var(--au-red)";
  return (
    <div className="border-b border-border bg-background/70 backdrop-blur-xl" data-testid="room-header">
      <div className="mx-auto flex max-w-[1440px] flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 sm:px-8">
        <div className="flex items-center gap-3">
          <AuroraLogo size={36} />
          <AuroraWordmark className="text-xl sm:text-2xl" />
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          {room.encrypted && <NoTraceTag />}
          <span data-testid="room-code">ID {room.room_id}</span>
          <span data-testid="room-languages">{[...new Set(room.participants.map((p) => p.language.toUpperCase()))].join(" · ")}</span>
          <span>VOICE {room.voice}</span>
          <span className="flex items-center gap-1.5" data-testid="room-connection-status">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: stColor, boxShadow: `0 0 8px ${stColor}` }} />
            {status}{rtt != null && ` · ${rtt}ms`}
          </span>
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <EncryptedBadge compact testId="room-encrypted-badge" />
          <SupportButton />
          <InviteQrDialog room={room} />
          {isHost ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="btn-danger !h-9 !px-4 text-xs" data-testid="room-end-call-button"><PhoneOff size={14} /> End<span className="hidden sm:inline"> call</span></button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-none">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display">End the call for everyone?</AlertDialogTitle>
                  <AlertDialogDescription>This SpeechFlow room will close permanently for everyone.{room.encrypted && " This is a no-trace room: every message will be permanently deleted."}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-none" data-testid="room-end-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onEnd} className="rounded-none bg-[color:var(--au-red)] text-white hover:bg-[#e02d23]" data-testid="room-end-confirm">End call</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <button onClick={onLeave} className="btn-ghost !h-9 !px-3 text-xs" data-testid="room-leave-button"><LogOut size={14} /> Leave</button>
          )}
        </div>
      </div>
      <div className="aurora-bar h-px opacity-70" />
    </div>
  );
};
